import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import './MaterialPlanningLanding.css';

function MaterialPlanningLanding() {
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('usage');
  const navigate = useNavigate();

  useEffect(() => {
    loadVessels();
  }, []);

  const loadVessels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/material-planning/vessels');
      setVessels(response.data || []);
    } catch (err) {
      console.error('Error loading vessels:', err);
      setError('Failed to load vessels. ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!window.confirm('Sync vessel usage from work orders, blends, and batches? This may take a few minutes.')) {
      return;
    }

    try {
      setSyncing(true);
      setError(null);
      const response = await axios.post('/api/material-planning/sync');
      alert(`Sync completed!\nWork Orders: ${response.data.workOrders.syncedCount}\nBlends: ${response.data.blends.syncedCount}\nBatches: ${response.data.batches.syncedCount}\nTotal: ${response.data.totalSynced}`);
      await loadVessels();
    } catch (err) {
      console.error('Error syncing:', err);
      setError('Sync failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleGenerateProjections = async () => {
    if (!window.confirm('Generate projections for all vessels? This may take a few minutes.')) {
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const response = await axios.post('/api/material-planning/generate-all-projections', {
        monthsAhead: 6
      });
      const successful = response.data.filter(r => r.success).length;
      alert(`Generated projections for ${successful} vessels`);
      await loadVessels();
    } catch (err) {
      console.error('Error generating projections:', err);
      setError('Generation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleVesselClick = (vesselSku) => {
    navigate(`/material-planning/${vesselSku}`);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/api/material-planning/export?format=csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vessel_usage_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Export failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // Filter and sort vessels
  const filteredVessels = vessels
    .filter(v =>
      !filter ||
      v.vessel_sku.toLowerCase().includes(filter.toLowerCase()) ||
      (v.vessel_name && v.vessel_name.toLowerCase().includes(filter.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'usage') {
        return (b.total_last_12_months || 0) - (a.total_last_12_months || 0);
      } else if (sortBy === 'sku') {
        return a.vessel_sku.localeCompare(b.vessel_sku);
      } else if (sortBy === 'name') {
        return (a.vessel_name || '').localeCompare(b.vessel_name || '');
      }
      return 0;
    });

  return (
    <div className="material-planning-landing">
      <div className="header">
        <h1>Material Planning - Vessel Usage</h1>
        <div className="header-actions">
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="btn-sync"
          >
            {syncing ? 'Syncing...' : 'Sync Usage Data'}
          </button>
          <button
            onClick={handleGenerateProjections}
            disabled={generating || loading}
            className="btn-generate"
          >
            {generating ? 'Generating...' : 'Generate Projections'}
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="btn-export"
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Filter by SKU or name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="sort-options">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="usage">Usage (High to Low)</option>
            <option value="sku">SKU</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filteredVessels.length === 0 ? (
        <div className="no-data">
          <p>No vessel data available.</p>
          <p>Click "Sync Usage Data" to import vessel usage from work orders, blends, and batches.</p>
        </div>
      ) : (
        <div className="vessel-grid">
          {filteredVessels.map(vessel => (
            <div
              key={vessel.vessel_sku}
              className="vessel-card"
              onClick={() => handleVesselClick(vessel.vessel_sku)}
            >
              <div className="vessel-header">
                <h3>{vessel.vessel_sku}</h3>
                {!vessel.is_active && <span className="inactive-badge">Inactive</span>}
              </div>

              <div className="vessel-name">
                {vessel.vessel_name || 'No description'}
              </div>

              <div className="vessel-stats">
                <div className="stat">
                  <label>Last 12 Months</label>
                  <value>{(vessel.total_last_12_months || 0).toLocaleString()} units</value>
                </div>
                <div className="stat">
                  <label>Avg Monthly</label>
                  <value>{(vessel.avg_monthly_usage || 0).toLocaleString()} units</value>
                </div>
              </div>

              <div className="vessel-dates">
                {vessel.first_used_date && (
                  <div className="date-info">
                    First used: {new Date(vessel.first_used_date).toLocaleDateString()}
                  </div>
                )}
                {vessel.last_used_date && (
                  <div className="date-info">
                    Last used: {new Date(vessel.last_used_date).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div className="view-details">
                Click for detailed analysis →
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="footer-info">
        <p>
          Total Vessels: {filteredVessels.length}
          {filter && ` (filtered from ${vessels.length})`}
        </p>
        <p className="help-text">
          Click on a vessel to view historical usage, projections, and trends.
        </p>
      </div>
    </div>
  );
}

export default MaterialPlanningLanding;
